import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsIOS(ios);

    // Don't show if already installed
    if (standalone) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // Android/Desktop: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS: show manual instructions after delay
    if (ios && !standalone) {
      setTimeout(() => setShowBanner(true), 5000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setShowBanner(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-3 md:p-4 animate-in slide-in-from-bottom duration-300" data-testid="pwa-install-banner">
      <div className="max-w-lg mx-auto bg-slate-800 text-white rounded-2xl shadow-2xl border border-slate-700 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <Smartphone size={22} className="text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Installa Schiliro CRM</p>
          {isIOS ? (
            <p className="text-[11px] text-slate-400 mt-0.5">
              Tocca <span className="inline-block px-1 py-0.5 bg-slate-700 rounded text-[10px]">Condividi</span> poi <span className="inline-block px-1 py-0.5 bg-slate-700 rounded text-[10px]">Aggiungi a Home</span>
            </p>
          ) : (
            <p className="text-[11px] text-slate-400 mt-0.5">Accedi rapidamente dal tuo telefono</p>
          )}
        </div>
        {!isIOS && deferredPrompt && (
          <Button size="sm" onClick={handleInstall} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold text-xs h-8 px-3" data-testid="pwa-install-btn">
            <Download size={14} className="mr-1" /> Installa
          </Button>
        )}
        <button onClick={handleDismiss} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors flex-shrink-0" data-testid="pwa-dismiss-btn">
          <X size={16} className="text-slate-400" />
        </button>
      </div>
    </div>
  );
};
